import { useEffect } from 'react';
import type { Result } from 'better-result';
import { CustomerId, TenantId } from '@finnest/power-admin-contracts';
import { InfrastructureError } from '@finnest/core/result';
import type { UnauthorizedError, ValidationError } from '@finnest/core/result';
import { browserHttp, fetchOperatorCustomerId } from '../runtime/power-admin-api.ts';
import { fetchOperatorEmail } from './operator-auth.ts';

export const INTERNAL_CUSTOMER_ID = CustomerId.make('internal');
export const INTERNAL_TENANT_ID = TenantId.make('internal');

type OperatorSessionFailure = InfrastructureError | UnauthorizedError | ValidationError;

export const operatorCustomerIdQueryOptions = {
  queryKey: ['power-admin', 'operator-session', 'customerId'] as const,
  queryFn: (): Promise<Result<CustomerId, OperatorSessionFailure>> =>
    fetchOperatorCustomerId(browserHttp()),
  staleTime: 'static' as const,
  retry: false,
};

export const operatorEmailQueryOptions = {
  queryKey: ['power-admin', 'operator-session', 'email'] as const,
  queryFn: (): Promise<Result<string, OperatorSessionFailure>> => fetchOperatorEmail(browserHttp()),
  staleTime: 'static' as const,
  retry: false,
};

export const customerIdFromSession = (
  result: Result<CustomerId, OperatorSessionFailure>,
): CustomerId =>
  result.match({
    ok: (customerId) => customerId,
    err: () => INTERNAL_CUSTOMER_ID,
  });

/**
 * How long the console waits between renewals: Power IAM's update age, the age past which its
 * session read renews the browser session and its cookie.
 */
export const SESSION_RENEWAL_INTERVAL_MS = 300_000;

// What counts as the operator working in the console.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;

/**
 * Renews the operator's Power IAM session while the operator works: on activity, at most once a
 * renewal interval, the console reads its session from Power IAM, which renews it and its cookie.
 * Power's own reads never renew it, so an idle console lets the session end on its idle window. A
 * session that has ended calls `onEnded`; a read that does not reach Power IAM is retried on the
 * next activity past the interval. A public page (`signedIn` false) renews nothing.
 */
export const useOperatorSessionRenewal = (signedIn: boolean, onEnded: () => void): void => {
  useEffect(() => {
    let renewedAt = Date.now();
    const renew = async (): Promise<void> => {
      const now = Date.now();
      if (!signedIn || now - renewedAt < SESSION_RENEWAL_INTERVAL_MS) return;
      renewedAt = now;
      const session = await fetchOperatorEmail(browserHttp());
      if (session.isErr() && !(session.error instanceof InfrastructureError)) onEnded();
    };
    const onActivity = (): void => {
      void renew();
    };
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    return (): void => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, onActivity);
    };
  }, [signedIn, onEnded]);
};
